import type { ITaskCard } from './types';

const PRIORITY_ORDER: Record<string, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

/**
 * Sort pending tasks by ownership: assigned to current user → unassigned →
 * assigned to others. Within each group, sort by priority (High → Medium → Low).
 */
export const sortPendingTasks = (
  tasks: ITaskCard[],
  currentUserGuid: string,
): ITaskCard[] => {
  if (!tasks.length) return tasks;

  return [...tasks].sort((a, b) => {
    const groupA = getOwnershipGroup(a, currentUserGuid);
    const groupB = getOwnershipGroup(b, currentUserGuid);

    if (groupA !== groupB) return groupA - groupB;

    // Within same group, sort by priority (high → medium → low)
    const orderA = PRIORITY_ORDER[a.priority] ?? 3;
    const orderB = PRIORITY_ORDER[b.priority] ?? 3;
    if (orderA !== orderB) return orderA - orderB;

    // Same priority — most recently updated first
    return (
      new Date(b.updatedAt ?? b.createdAt).getTime() -
      new Date(a.updatedAt ?? a.createdAt).getTime()
    );
  });
};

/**
 * Sort completed tasks by most recently completed first.
 * Falls back to createdAt when completedAt is missing.
 */
export const sortCompletedTasks = (tasks: ITaskCard[]): ITaskCard[] => {
  if (!tasks.length) return tasks;

  return [...tasks].sort(
    (a, b) =>
      new Date(b.completedAt ?? b.createdAt).getTime() -
      new Date(a.completedAt ?? a.createdAt).getTime(),
  );
};

/** 0 = assigned to me, 1 = unassigned, 2 = assigned to others */
export const getOwnershipGroup = (
  task: ITaskCard,
  currentUserGuid: string,
): number => {
  if (currentUserGuid && task.assignedToGuid === currentUserGuid) return 0;
  if (!task.assignedToGuid) return 1;
  return 2;
};
